import { useCallback, useState, type ReactNode } from "react";
import { MessageComposer } from "./MessageComposer";
import { ManualCommunicationForm } from "./ManualCommunicationForm";
import { MessagesList } from "./MessagesList";
import {
  MessagesEmptyState,
  type MessagesEmptyStateVariant
} from "./MessagesEmptyState";
import { MessagesLoadingState } from "./MessagesLoadingState";
import {
  useInboxDetailPanelData,
  useMessagesScroll
} from "../hooks/useMessagesScroll";
import type { ComposerTab } from "./constants";
import { useReplyTarget } from "../hooks/useReplyTarget";
import { useConversationSupportAgent } from "../hooks/useConversationSupportAgent";

interface ConversationPaneMeta {
  contactId?: string;
  contactName: string;
  /** The applicant whose AI support agent this conversation's chat belongs to. */
  supportAgent?: { applicantId: number; active: boolean };
}

interface ConversationPaneProps {
  conversationId?: string;
  /** Selected channel, owned by the host (URL on /inbox, chip state in the pop-up). */
  activeChannel: ComposerTab;
  onChannelChange: (channel: ComposerTab) => void;
  /** Pre-seeds the email reply banner. Was the `?reply_to` search param. */
  replyToMessageId?: string;
  /** Fired once `replyToMessageId` has been resolved (or rejected). */
  onReplyConsumed?: () => void;
  /** Load only `activeChannel`'s messages instead of the combined thread. */
  filterByChannel?: boolean;
  /** Allow the Manual (note) composer. Off for the pop-up, which has no Manual chip. */
  allowManual?: boolean;
  /** Hide the composer's own channel tabs when the host renders chips itself. */
  hideComposerTabs?: boolean;
  emptyStateDescription?: string;
  /** Which of the design's empty states to draw. Defaults to the /inbox one. */
  emptyStateVariant?: MessagesEmptyStateVariant;
  /** Rendered under the composer (the pop-up's "also sent via SMS" note). */
  footer?: ReactNode;
  className?: string;
  scrollClassName?: string;
  renderHeader?: (meta: ConversationPaneMeta) => ReactNode;
}

const DEFAULT_SCROLL_CLASS =
  "flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-alpha-100 scrollbar-track-transparent hover:scrollbar-thumb-alpha-200";

/**
 * A conversation's thread plus composer, with no page chrome of its own — the
 * reusable half of the Inbox's DetailPanel. The Inbox route wraps it with
 * DetailHeader; the floating message pop-up (CRM-1133) wraps it with its own
 * header and chip row and opts into single-channel mode.
 */
export const ConversationPane = ({
  conversationId,
  activeChannel,
  onChannelChange,
  replyToMessageId,
  onReplyConsumed,
  filterByChannel = false,
  allowManual = false,
  hideComposerTabs = false,
  emptyStateDescription,
  emptyStateVariant = "compact",
  footer,
  className = "flex-1 bg-background-800 flex flex-col h-full",
  scrollClassName = DEFAULT_SCROLL_CLASS,
  renderHeader
}: ConversationPaneProps) => {
  // Selected SMS "from" number (from the composer's SmsRoutingSelect), shared
  // with the suggestion card so it sends from the same number.
  const [smsFromPhoneId, setSmsFromPhoneId] = useState<string>("");

  const {
    targetMessageId,
    initialUnreadCount,
    draftContactId,
    isDraft,
    isAnchorMode,
    messages,
    pageCount,
    lastMessageKey,
    isLoadingMessages,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    contactId,
    contactName
  } = useInboxDetailPanelData(conversationId, activeChannel, {
    filterByChannel
  });

  const { scrollContainerRef, handleLoadMoreBefore } = useMessagesScroll({
    conversationId,
    pageCount,
    lastMessageKey,
    isAnchorMode,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage
  });

  const handleLoadMoreAfter = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { replyTarget, setReplyTarget } = useReplyTarget({
    conversationId,
    activeChannel,
    replyToMessageId,
    messages,
    contactName,
    onReplyConsumed
  });

  const supportAgent = useConversationSupportAgent(contactId, messages);

  if (!conversationId && !isDraft) return null;

  const showEmptyState =
    isDraft || (!isLoadingMessages && messages.length === 0);
  const showMessagesList =
    !isDraft && !isLoadingMessages && messages.length > 0;
  const composerDraftContactId = isDraft ? draftContactId : undefined;
  const showManualComposer = allowManual && activeChannel === "note";

  return (
    <div className={className}>
      {renderHeader?.({ contactId, contactName, supportAgent })}

      <div ref={scrollContainerRef} className={scrollClassName}>
        {!isDraft && isLoadingMessages ? <MessagesLoadingState /> : null}
        {showEmptyState ? (
          <MessagesEmptyState
            description={emptyStateDescription}
            variant={emptyStateVariant}
          />
        ) : null}
        {showMessagesList ? (
          <MessagesList
            messages={messages}
            conversationId={conversationId}
            contactId={contactId}
            contactName={contactName}
            smsFromPhoneId={smsFromPhoneId}
            highlightMessageId={targetMessageId}
            unreadCount={initialUnreadCount}
            onLoadMoreBefore={handleLoadMoreBefore}
            onLoadMoreAfter={isAnchorMode ? handleLoadMoreAfter : undefined}
            hasMoreBefore={hasPreviousPage}
            hasMoreAfter={isAnchorMode ? hasNextPage : false}
            isLoadingBefore={isFetchingPreviousPage}
            isLoadingAfter={isFetchingNextPage}
            onReply={setReplyTarget}
          />
        ) : null}
      </div>

      {showManualComposer ? (
        <ManualCommunicationForm
          conversationId={conversationId}
          contactId={contactId}
          draftContactId={composerDraftContactId}
          activeTab={activeChannel}
          onTabChange={onChannelChange}
        />
      ) : (
        <MessageComposer
          conversationId={conversationId}
          contactId={contactId}
          draftContactId={composerDraftContactId}
          activeTab={activeChannel}
          onTabChange={onChannelChange}
          hideTabs={hideComposerTabs}
          replyTarget={replyTarget}
          onCancelReply={() => setReplyTarget(null)}
          onMessageSent={() => setReplyTarget(null)}
          onSmsSelectionChange={(phoneNumberId) =>
            setSmsFromPhoneId(phoneNumberId)
          }
        />
      )}

      {footer}
    </div>
  );
};
